import cache from '../storage/cache';
import queue from '../storage/queue';
import { AnalyticsConfig } from '../types/configs';
import logging from '../utils/logging';
import {
  analyticsGlobal,
  getQbeTrack,
  HydratedQbeTrack,
  setQbeTrackConfigGetter,
} from './qbeTrack';

// Re-export for backward compatibility
export { getQbeTrack, type HydratedQbeTrack };

const { logLevelParam } = logging.getLogOptions();
/**
 * default config object
 * status defaults to `true`, this means analytics is on by default
 */
let config: AnalyticsConfig = {
  status: true,
  dataLayerName: 'dataLayer',
  initialData: {},
  environment: '',
  isAuthenticated: false,
  shouldReset: false,
  brand: '',
  appName: '',
  appPartner: '',
  appVersion: '',
  appEnv: '',
  appType: '',
  appProduct: '',
  basePagePath: '',
  formEngagement: {
    formEngaged: false,
    formCompleted: false,
  },
  analyticsDataObject: {
    dataLayer: [],
  },
  dequeueLocation: [],
  dataLayerKey: {},
  unitTestMode: false,
  cache: {
    set: () => {},
    clear: () => {},
  },
  analyticsEventBus: {
    subscribeEvents: {},
    globalSubscribers: [] as Function[],
    eventBusRecords: [] as object[],
  },
};

/**
 * setConfig
 *
 * Merges partial runtime configuration into the current QAL config.
 *
 * This is a low-level helper used by QAL internals and advanced consumers. It performs a shallow
 * merge, so nested objects such as `analyticsTime`, `analyticsMilestones`, and `formEngagement`
 * should be supplied with all keys that must be preserved.
 *
 * @param newConfig - Partial config values to merge into the current runtime config.
 * @returns The updated QAL runtime configuration.
 */
export const setConfig = (newConfig: object) => {
  config = {
    ...config,
    ...newConfig,
  };

  return config;
};

/**
 * getConfig
 *
 * Returns the current mutable QAL runtime configuration.
 *
 * Use this for diagnostics or integration adapters that need current QAL metadata, feature flags,
 * milestone state, or data-layer configuration. Treat the returned object as internal runtime state;
 * prefer `configAnalytics` and public `analytics` methods for normal app code.
 *
 * @returns The current QAL runtime configuration object.
 */
export const getConfig = () => config;

// Initialize qbeTrack config getters
if (logLevelParam !== 'reset') {
  setQbeTrackConfigGetter(
    () => config,
    () => ({
      dataLayer: config.analyticsDataObject.dataLayer,
      activePath: config.analyticsDataObject.activePagePath,
      initialData: config.initialData,
      appEnv: config.appEnv,
      isAuthenticated: config.isAuthenticated,
      shouldReset: config.shouldReset,
      dataLayerName: config.dataLayerName,
      appName: config.appName,
      appPartner: config.appPartner,
      appVersion: config.appVersion,
      basePagePath: config.basePagePath,
      unitTestMode: config.unitTestMode,
    })
  );
}

/**
 * Create Cache
 * NOTE: This is currently hardcoded but in future could be a passed via configuration
 */
const cacheConfig = cache(
  typeof window !== 'undefined' && window.sessionStorage
);

const existing = cacheConfig.getCacheItem('dataLayer');

if (Array.isArray(existing)) {
  existing.forEach((item: object) => config.dequeueLocation.push(item));
}

const dequeueLocation =
  config.dequeueLocation ?? analyticsGlobal.dataLayer ?? [];
const analyticsDataObject: NonNullable<AnalyticsConfig['analyticsDataObject']> =
  config.analyticsDataObject ?? { dataLayer: [] };

analyticsDataObject.dataLayer =
  analyticsGlobal.dataLayer ?? config.analyticsDataObject?.dataLayer ?? [];

analyticsGlobal.dataLayer = config.analyticsDataObject.dataLayer;
const sessionStorageKey = '__qbe_analytics';
export const storageKey = `${config.appName}-${config.brand}`;

const queueConfig = queue({
  appName: config.appName,
  appPartner: config.appPartner,
  appVersion: config.appVersion,
  appEnv: config.appEnv,
  dequeueLocation,
  analyticsDataObject, // NOTE: better name, should this be passed in ?
  sessionStorageKey,
  cache: cacheConfig, // NOTE: using own cache atm, maybe look at being able to have a cache passed in
});

/**
 * merge configuration
 * helper for merging required configuration items
 */
setConfig({
  ...config,
  analyticsDataObject,
  cache: cacheConfig,
  queue: queueConfig,
  sessionStorageKey,
});
